import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaQrcode, FaSearch, FaFilter, FaEye, FaEdit, FaTrash, FaPhone, FaEnvelope } from 'react-icons/fa';
import { format } from 'date-fns';
import { enquiryAPI } from '../../services/api';
import ConfirmationModal from '../ConfirmationModal';

const Enquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEnquiries, setTotalEnquiries] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [enquiryToDelete, setEnquiryToDelete] = useState(null);
  const navigate = useNavigate();

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'follow_up', label: 'Follow Up' },
    { value: 'converted', label: 'Converted' },
    { value: 'lost', label: 'Lost' }
  ];

  const sourceOptions = [
    { value: '', label: 'All Sources' },
    { value: 'qr_code', label: 'QR Code' },
    { value: 'manual', label: 'Manual' },
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'other', label: 'Other' }
  ];

  const fetchEnquiries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await enquiryAPI.getAll(currentPage, 10, statusFilter || undefined, sourceFilter || undefined, searchTerm || undefined);
      setEnquiries(response.data.enquiries);
      setTotalPages(response.data.pages);
      setTotalEnquiries(response.data.total);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter, sourceFilter]);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const handleStatusUpdate = async (enquiryId, newStatus) => {
    try {
      await enquiryAPI.updateStatus(enquiryId, newStatus);
      fetchEnquiries();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDelete = async (enquiryId) => {
    setEnquiryToDelete({ id: enquiryId });
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    if (!enquiryToDelete) return;

    try {
      await enquiryAPI.delete(enquiryToDelete.id);
      fetchEnquiries();
      setShowDeleteConfirmation(false);
      setEnquiryToDelete(null);
    } catch (error) {
      console.error('Error deleting enquiry:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      follow_up: 'bg-orange-100 text-orange-800',
      converted: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getSourceIcon = (source) => {
    const icons = {
      qr_code: '📱',
      manual: '✏️',
      website: '🌐',
      referral: '👥',
      other: '📋'
    };
    return icons[source] || '📋';
  };

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8 bg-gray-900 min-h-screen">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-4 sm:space-y-0">
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Enquiries</h1>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            onClick={() => navigate('/enquiries/qr-code')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-lg text-sm sm:text-base"
          >
            <FaQrcode className="h-4 w-4" />
            <span className="hidden sm:inline">Generate QR Code</span>
            <span className="sm:hidden">QR Code</span>
          </button>
          <button
            onClick={() => navigate('/enquiries/create')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-lg text-sm sm:text-base"
          >
            <FaPlus className="h-4 w-4" />
            <span className="hidden sm:inline">Add Enquiry</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 mb-4 sm:mb-6 border border-gray-700">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by name, email, phone, or course..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 text-sm sm:text-base"
              />
            </div>
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="bg-gray-700 hover:bg-gray-600 text-gray-200 px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors border border-gray-600 text-sm sm:text-base"
          >
            <FaFilter className="h-4 w-4" />
            <span className="hidden sm:inline">Filters</span>
            <span className="sm:hidden">Filter</span>
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white text-sm sm:text-base"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value} className="bg-gray-700 text-white">
                  {option.label}
                </option>
              ))}
            </select>
            
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white text-sm sm:text-base"
            >
              {sourceOptions.map(option => (
                <option key={option.value} value={option.value} className="bg-gray-700 text-white">
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Enquiries List */}
      <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden border border-gray-700">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-300">Loading enquiries...</p>
          </div>
        ) : enquiries.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-300">No enquiries found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Enquiry
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                      Contact
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                      Course Interest
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                      Status
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                      Source
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                      Date
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {enquiries.map((enquiry) => (
                    <tr key={enquiry._id} className="hover:bg-gray-700 transition-colors">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-white">
                            {enquiry.first_name} {enquiry.last_name}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-400">
                            ID: {enquiry._id.slice(-8)}
                          </div>
                          {/* Mobile-only contact info */}
                          <div className="md:hidden text-xs text-gray-400 mt-1">
                            <div className="flex items-center gap-1">
                              <FaEnvelope className="text-gray-400" />
                              <span>{enquiry.email}</span>
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              <FaPhone className="text-gray-400" />
                              <span>{enquiry.phone}</span>
                            </div>
                          </div>
                          {/* Mobile-only course info */}
                          <div className="lg:hidden text-xs text-gray-400 mt-1">
                            {enquiry.interested_course || 'Not specified'}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden md:table-cell">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 text-sm">
                            <FaEnvelope className="text-gray-400" />
                            <a href={`mailto:${enquiry.email}`} className="text-blue-400 hover:text-blue-300 hover:underline">
                              {enquiry.email}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <FaPhone className="text-gray-400" />
                            <a href={`tel:${enquiry.phone}`} className="text-blue-400 hover:text-blue-300 hover:underline">
                              {enquiry.phone}
                            </a>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden lg:table-cell">
                        <div className="text-sm text-white">
                          {enquiry.interested_course || 'Not specified'}
                        </div>
                        {enquiry.preferred_batch_timing && (
                          <div className="text-sm text-gray-400">
                            {enquiry.preferred_batch_timing}
                          </div>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden sm:table-cell">
                        <select
                          value={enquiry.status}
                          onChange={(e) => handleStatusUpdate(enquiry._id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(enquiry.status)} border-0 focus:ring-2 focus:ring-blue-500`}
                        >
                          {statusOptions.slice(1).map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden lg:table-cell">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getSourceIcon(enquiry.source)}</span>
                          <span className="text-sm text-white capitalize">
                            {enquiry.source.replace('_', ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-400 hidden md:table-cell">
                        {format(new Date(enquiry.created_at), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-1 sm:gap-2">
                          <button
                            onClick={() => navigate(`/enquiries/${enquiry._id}`)}
                            className="p-1 sm:p-2 text-blue-400 hover:text-blue-300 transition-colors rounded-md hover:bg-gray-700"
                            title="View Details"
                          >
                            <FaEye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/enquiries/${enquiry._id}/edit`)}
                            className="p-1 sm:p-2 text-green-400 hover:text-green-300 transition-colors rounded-md hover:bg-gray-700"
                            title="Edit"
                          >
                            <FaEdit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(enquiry._id)}
                            className="p-1 sm:p-2 text-red-400 hover:text-red-300 transition-colors rounded-md hover:bg-gray-700"
                            title="Delete"
                          >
                            <FaTrash className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-700 px-4 py-3 flex items-center justify-between border-t border-gray-600 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-200 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-200 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-300">
                      Showing <span className="font-medium text-white">{(currentPage - 1) * 10 + 1}</span> to{' '}
                      <span className="font-medium text-white">
                        {Math.min(currentPage * 10, totalEnquiries)}
                      </span>{' '}
                      of <span className="font-medium text-white">{totalEnquiries}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-blue-600 border-blue-500 text-white'
                              : 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={`Are you sure you want to delete this enquiry? This action cannot be undone.`}
      />
    </div>
  );
};

export default Enquiries; 